/**
 * Shared helpers for the board views (table / kanban / calendar / item panel).
 * Role detection maps a board's generic typed columns onto the semantic slots
 * the kanban & calendar views need (a status column, a date column, etc.).
 */

import React from 'react';
import {
  DocumentTextIcon,
  EnvelopeIcon,
  PhotoIcon,
  TableCellsIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import type { BoardColumn, BoardFull, BoardRow, StatusOption, AttachmentKind } from './types';

// ---- column role resolution ------------------------------------------------
export interface BoardRoles {
  titleCol: BoardColumn | null;     // first text column — used as the row's headline
  statusCol: BoardColumn | null;    // first status column — kanban lanes
  dateCol: BoardColumn | null;      // first date column — calendar placement
  personCol: BoardColumn | null;    // first person column — avatar
  progressCol: BoardColumn | null;  // first number column shown as a % bar
  chipCols: BoardColumn[];          // status/dropdown columns shown as chips on cards
}

const isProgress = (c: BoardColumn) =>
  c.kind === 'number' && (c.config?.suffix === '%' || (c.config as { display?: string })?.display === 'progress');

export function resolveRoles(columns: BoardColumn[]): BoardRoles {
  return {
    titleCol: columns.find(c => c.kind === 'text' && !c.config?.variant) || null,
    statusCol: columns.find(c => c.kind === 'status') || null,
    dateCol: columns.find(c => c.kind === 'date') || null,
    personCol: columns.find(c => c.kind === 'person') || null,
    progressCol: columns.find(isProgress) || null,
    chipCols: columns.filter(c => c.kind === 'status' || c.kind === 'dropdown'),
  };
}

export function rowTitle(row: BoardRow, titleCol: BoardColumn | null): string {
  if (titleCol) {
    const v = row.values[titleCol.id];
    if (v != null && String(v).trim()) return String(v);
  }
  return '(Sans titre)';
}

export function statusOption(col: BoardColumn | null, value: unknown): StatusOption | null {
  if (!col) return null;
  const opts = (col.config?.options || []) as StatusOption[];
  return opts.find(o => o.id === value) || null;
}

export const allRows = (board: BoardFull): BoardRow[] => board.rows;

// ---- date helpers ----------------------------------------------------------
export const isoOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function dueState(iso: unknown): { label: string; soon: boolean; empty: boolean } {
  if (!iso || typeof iso !== 'string') return { label: '—', soon: false, empty: true };
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return { label: '—', soon: false, empty: true };
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  const label = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  return { label, soon: days >= 0 && days <= 10, empty: false };
}

// ---- person helpers --------------------------------------------------------
export const personInitials = (label: string) =>
  label.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w.charAt(0)).join('').toUpperCase() || '?';

export const personColor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `oklch(0.62 0.15 ${h % 360})`;
};

// ---- attachment metadata ---------------------------------------------------
export const ATTACH_META: Record<AttachmentKind, { cls: string; Icon: React.FC<{ className?: string }> }> = {
  pdf:   { cls: 'bd-ic-pdf',   Icon: DocumentTextIcon },
  email: { cls: 'bd-ic-email', Icon: EnvelopeIcon },
  photo: { cls: 'bd-ic-photo', Icon: PhotoIcon },
  word:  { cls: 'bd-ic-word',  Icon: DocumentTextIcon },
  excel: { cls: 'bd-ic-excel', Icon: TableCellsIcon },
  file:  { cls: 'bd-ic-file',  Icon: PaperClipIcon },
};

export const fmtBytes = (b: number | null | undefined) => {
  if (b == null) return '';
  if (b < 1024) return `${b} o`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(0)} Ko`;
  return `${(b / 1_048_576).toFixed(1).replace('.', ',')} Mo`;
};
